import { helpers, CONST } from "@ppb/bet-engine";
import { BetslipApplicationState } from "../store/state/BetslipApplicationState";
import { getExchangeRunnerTree } from "../store/state/exchange-markets/exchange-markets-selectors";
import { createRunnerPotentialBetsSelector } from "../store/state/exchange-betting/runner-potential-bets-selectors";
import { getExchangePotentialState } from "../store/state/exchange-betting-potential/exchange-betting-potential-selectors";
import { LADDER_DEFAULT_MIN_SIZE } from "../config/bet-engine-config";
import {
  getHasEditedUnmatchedBet,
  getBetslipExchangeContext,
  getBetslipExchangeReport,
} from "../store/state/betslip-module/betslip-module-selectors";

import { i18n } from "../helpers/i18n";

import type { MatchMeOddsRange } from "../components/MatchMe/types";
import type {
  BetslipSession,
  ExchangeBetslipCapabilities,
  ExchangeBetslipPreferences,
  ExchangeCurrencyDetails,
} from "../contract";

const getRunnerPotentialBets = createRunnerPotentialBetsSelector();

export function isBPEAvailable(
  appState: BetslipApplicationState,
  session: BetslipSession,
  capabilities: ExchangeBetslipCapabilities,
  preferences: ExchangeBetslipPreferences,
): boolean {
  try {
    if (!session.loggedIn) return false;

    if (!capabilities.bestPriceExecution.enabled) return false;

    if (!preferences.bestPriceExecutionLimit) return false;

    const betslipContext = getBetslipExchangeContext(appState);

    if (!betslipContext) return false;

    const runnerTree = getExchangeRunnerTree(appState, betslipContext.runner);

    if (!runnerTree) return false;

    const {
      market: { marketType, bettingType },
    } = runnerTree;

    const [potentialBet] = getRunnerPotentialBets(appState, betslipContext.runner);

    const exchangeReport = getBetslipExchangeReport(appState);
    const isBetReceipt = !!exchangeReport;

    if (!potentialBet && !isBetReceipt) return false;

    const { isSp, isOddsLimitActive } =
      getExchangePotentialState(appState, betslipContext.runner, potentialBet?.side) ?? {};

    const isSpBet = isBetReceipt
      ? exchangeReport?.spSize != null
      : !!isSp && (potentialBet?.price === undefined || !!isOddsLimitActive);

    if (isSpBet) return false;

    const isMatchMeBettingBehaviour =
      helpers.getBettingBehaviour(marketType, bettingType) === CONST.BETTING_BEHAVIOUR.ODDS_GENERIC ||
      helpers.getBettingBehaviour(marketType, bettingType) === CONST.BETTING_BEHAVIOUR.ODDS_EACH_WAY;

    // When viewing a bet receipt, the receipt's own side is what matters - a potential bet for the
    // opposite side may coexist on the same runner (e.g. hedging) and must not override this check.
    const isBackBet = isBetReceipt ? exchangeReport?.side === "BACK" : potentialBet?.side === "BACK";

    return isMatchMeBettingBehaviour && isBackBet;
  } catch {
    return false;
  }
}

/**
 * Determines whether the "Bet with MatchMe" option should be offered for the current exchange
 * bet receipt, mirroring the legacy `isEligibleToBetWithMatchMe` eligibility rules.
 *
 * The option is shown when:
 *  - the bet has an unmatched (or partially matched) portion left to match;
 *  - best price execution is available for the bet;
 *  - the user has a MatchMe preference available (matchMe.available) that is currently turned off (!matchMe.enabled);
 *  - the guaranteed matching price is within the current best market price for the bet's side;
 *  - the remaining unmatched size meets the minimum stake.
 */
export function shouldDisplayBetWithMatchMe(
  appState: BetslipApplicationState,
  currencyDetails: ExchangeCurrencyDetails,
  session: BetslipSession,
  capabilities: ExchangeBetslipCapabilities,
  preferences: ExchangeBetslipPreferences,
): boolean {
  const exchangeReport = getBetslipExchangeReport(appState);

  if (!exchangeReport) {
    return false;
  }

  if (getHasEditedUnmatchedBet(appState)) {
    return false;
  }

  const { runner: runnerUrn, side } = exchangeReport;
  const remnantUnmatchedSize = exchangeReport.unmatched?.size;

  // Bet must have an unmatched (or partially matched) portion left to match with MatchMe.
  const isFullOrPartialUnmatched = !!remnantUnmatchedSize;

  const runnerTree = getExchangeRunnerTree(appState, runnerUrn);
  const priceAtPlacement = getExchangePotentialState(appState, runnerUrn, side)?.priceAtPlacement;

  // MatchMe can only match at prices at least as good as the current best market price for the
  // bet's side, so the guaranteed matching price (priceAtPlacement) must be within the best price.
  const runnerBestPrice = side === "BACK" ? runnerTree?.runner?.back?.[0]?.price : runnerTree?.runner?.lay?.[0]?.price;

  const isPriceAtPlacementValid = !!(
    priceAtPlacement !== undefined &&
    runnerBestPrice !== undefined &&
    priceAtPlacement <= runnerBestPrice
  );

  const minimumStake = currencyDetails.minStake ?? LADDER_DEFAULT_MIN_SIZE;
  const isUnmatchedSizeValid = !!(
    remnantUnmatchedSize !== undefined &&
    minimumStake != null &&
    remnantUnmatchedSize >= minimumStake
  );

  return !!(
    isFullOrPartialUnmatched &&
    isBPEAvailable(appState, session, capabilities, preferences) &&
    preferences.matchMe.available &&
    !preferences.matchMe.enabled &&
    isPriceAtPlacementValid &&
    isUnmatchedSizeValid
  );
}

export const getOddsRangeLabel = (oddsRange: MatchMeOddsRange, price?: number) => {
  const label = i18n({
    key: "I18N.MATCH_ME.ODDS_RANGE",
    interpolationValues: oddsRange ? { min: oddsRange.min, max: oddsRange.max } : undefined,
  });

  return oddsRange ? label : label + (price || "");
};
